import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';
import { deleteCompany } from '../services/companyService';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';

const SuperAdminCompanies = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const companiesPerPage = 10;
  const [filters, setFilters] = useState({
    status: 'all',
    industry: 'all',
    search: ''
  });
  const [stats, setStats] = useState({
    totalCompanies: 0,
    verifiedCompanies: 0,
    pendingCompanies: 0,
    totalJobs: 0
  });
  const [deletingCompany, setDeletingCompany] = useState(null);

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    filterCompanies();
    setCurrentPage(1); // Reset to first page when filters change
  }, [companies, filters]);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const companiesSnapshot = await getDocs(collection(db, 'companies'));
      const jobsSnapshot = await getDocs(collection(db, 'jobs'));

      const companiesData = companiesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Count jobs per company - use userId as companyId
      const jobCounts = {};
      jobsSnapshot.docs.forEach(jobDoc => {
        const job = jobDoc.data();
        // Jobs are linked to companies via userId field
        const companyKey = job.companyId || job.userId;
        if (companyKey) {
          jobCounts[companyKey] = (jobCounts[companyKey] || 0) + 1;
        }
      });

      console.log('Job counts by company:', jobCounts);

      // Add job counts to companies - check both company.id and company.userId
      const companiesWithJobCounts = companiesData.map(company => {
        const count = jobCounts[company.id] || jobCounts[company.userId] || 0;
        console.log(`Company ${company.companyName || company.name}: ${count} jobs`);
        return {
          ...company,
          jobCount: count
        };
      });

      setCompanies(companiesWithJobCounts);

      // Calculate stats
      const totalCompanies = companiesWithJobCounts.length;
      const verifiedCompanies = companiesWithJobCounts.filter(company => company.isVerified).length;
      const pendingCompanies = companiesWithJobCounts.filter(company => !company.isVerified).length;
      const totalJobs = jobsSnapshot.size;

      setStats({
        totalCompanies,
        verifiedCompanies,
        pendingCompanies,
        totalJobs
      });

    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterCompanies = () => {
    let filtered = [...companies];

    if (filters.status !== 'all') {
      if (filters.status === 'verified') {
        filtered = filtered.filter(company => company.isVerified);
      } else if (filters.status === 'pending') {
        filtered = filtered.filter(company => !company.isVerified);
      }
    }

    if (filters.industry !== 'all') {
      filtered = filtered.filter(company => company.industry === filters.industry);
    }

    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(company => {
        const locationText = `${company.city || ''} ${company.state || ''} ${company.country || ''} ${company.location || ''}`.toLowerCase();

        return company.name?.toLowerCase().includes(searchTerm) ||
          company.companyName?.toLowerCase().includes(searchTerm) ||
          company.industry?.toLowerCase().includes(searchTerm) ||
          locationText.includes(searchTerm);
      });
    }

    setFilteredCompanies(filtered);
  };

  const handleCompanyClick = (company) => {
    setSelectedCompany(company);
    setShowCompanyModal(true);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString();
  };

  const getUniqueIndustries = () => {
    const industries = companies.map(company => company.industry).filter(Boolean);
    return [...new Set(industries)];
  };

  // Calculate pagination
  const indexOfLastCompany = currentPage * companiesPerPage;
  const indexOfFirstCompany = indexOfLastCompany - companiesPerPage;
  const currentCompanies = filteredCompanies.slice(indexOfFirstCompany, indexOfLastCompany);
  const totalPages = Math.ceil(filteredCompanies.length / companiesPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteCompany = async (companyId, companyName) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${companyName}?\n\nThis will permanently delete:\n- Company profile from database\n- All company data\n\nThis action cannot be undone.`
    );

    if (!confirmDelete) return;

    setDeletingCompany(companyId);

    try {
      const result = await deleteCompany(companyId);

      if (result.success) {
        // Remove company from local state
        setCompanies(prev => prev.filter(c => c.id !== companyId));
        setFilteredCompanies(prev => prev.filter(c => c.id !== companyId));

        // Close modal if this company was selected
        if (selectedCompany?.id === companyId) {
          setShowCompanyModal(false);
          setSelectedCompany(null);
        }

        // Update stats
        setStats(prev => ({
          ...prev,
          totalCompanies: prev.totalCompanies - 1
        }));

        alert('Company deleted successfully!');
      } else {
        alert(`Failed to delete company: ${result.error}`);
      }
    } catch (error) {
      console.error('Error deleting company:', error);
      alert('An error occurred while deleting the company. Please try again.');
    } finally {
      setDeletingCompany(null);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Companies Management</h1>
        <p className="text-gray-600">Monitor and manage all companies on the platform</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
          <div className="flex items-center">
            <div className="p-3 bg-blue-500 rounded-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Companies</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalCompanies}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
          <div className="flex items-center">
            <div className="p-3 bg-purple-500 rounded-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Jobs</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalJobs}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search Companies</label>
            <input
              type="text"
              placeholder="Search by name, industry, location..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>


          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Industry</label>
            <select
              value={filters.industry}
              onChange={(e) => setFilters(prev => ({ ...prev, industry: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Industries</option>
              {getUniqueIndustries().map(industry => (
                <option key={industry} value={industry}>{industry}</option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilters({ status: 'all', industry: 'all', search: '' })}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Companies List */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">
            All Companies ({filteredCompanies.length})
          </h2>
        </div>

        {filteredCompanies.length > 0 ? (
          <>
            <div className="divide-y divide-gray-200">
              {currentCompanies.map((company) => (
                <div
                  key={company.id}
                  onClick={() => handleCompanyClick(company)}
                  className="p-4 sm:p-6 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                          {(company.companyName || company.name)?.charAt(0)?.toUpperCase() || 'C'}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 hover:text-blue-600">
                            {company.companyName || company.name}
                          </h3>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                        <div className="flex items-center text-gray-600">
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                          </svg>
                          <span className="text-sm">{company.industry || 'Not specified'}</span>
                        </div>

                        <div className="flex items-center text-gray-600">
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span className="text-sm">
                            {company.city || company.state || company.country
                              ? `${company.city || ''}, ${company.state || ''}, ${company.country || ''}`.replace(/^,\s*|,\s*$/g, '').replace(/,\s*,/g, ',')
                              : company.location || 'Not specified'
                            }
                          </span>
                        </div>

                        <div className="flex items-center text-gray-600">
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          <span className="text-sm">{company.companySize || 'Not specified'}</span>
                        </div>

                        <div className="flex items-center text-gray-600">
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                          </svg>
                          <span className="text-sm">{company.jobCount} Jobs Posted</span>
                        </div>
                      </div>

                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                        {company.description?.substring(0, 150)}...
                      </p>

                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-sm">
                        <span className="text-gray-500">Registered: {formatDate(company.createdAt)}</span>
                        <div className="flex flex-wrap items-center gap-2">
                          {company.website && (
                            <a
                              href={company.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 font-medium"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Visit Website
                            </a>
                          )}
                          {company.jobCount > 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate('/superadmin/jobs', {
                                  state: {
                                    companyFilter: company.userId || company.id,
                                    companyName: company.companyName || company.name
                                  }
                                });
                              }}
                              className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center gap-1"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                              </svg>
                              View Jobs ({company.jobCount})
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="w-full sm:w-auto sm:ml-4 flex items-center justify-between sm:justify-start gap-2 mt-2 sm:mt-0">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCompany(company.id, company.companyName || company.name);
                        }}
                        disabled={deletingCompany === company.id}
                        className={`w-full sm:w-auto px-3 py-2 rounded-lg transition-colors flex items-center justify-center gap-1 ${deletingCompany === company.id
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : 'bg-red-600 hover:bg-red-700 text-white'
                          }`}
                        title="Delete company"
                      >
                        {deletingCompany === company.id ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span className="text-sm">Deleting...</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            <span className="text-sm">Delete</span>
                          </>
                        )}
                      </button>
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="p-4 sm:p-6 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-sm text-gray-700 text-center sm:text-left">
                    Showing <span className="font-semibold">{indexOfFirstCompany + 1}</span> to{' '}
                    <span className="font-semibold">{Math.min(indexOfLastCompany, filteredCompanies.length)}</span> of{' '}
                    <span className="font-semibold">{filteredCompanies.length}</span> companies
                  </div>

                  <div className="flex items-center justify-between sm:justify-center gap-2 w-full sm:w-auto">
                    {/* Previous Button */}
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`px-3 py-2 rounded-lg font-medium transition-colors text-sm ${currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                    >
                      Previous
                    </button>

                    {/* Page Numbers - visible on desktop/tablet, hidden on mobile */}
                    <div className="hidden sm:flex items-center gap-1">
                      {[...Array(totalPages)].map((_, index) => {
                        const pageNumber = index + 1;
                        if (
                          pageNumber === 1 ||
                          pageNumber === totalPages ||
                          (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={pageNumber}
                              onClick={() => handlePageChange(pageNumber)}
                              className={`w-10 h-10 rounded-lg font-medium transition-colors text-sm ${currentPage === pageNumber
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                              {pageNumber}
                            </button>
                          );
                        } else if (
                          pageNumber === currentPage - 2 ||
                          pageNumber === currentPage + 2
                        ) {
                          return (
                            <span key={pageNumber} className="px-2 text-gray-500 text-sm">
                              ...
                            </span>
                          );
                        }
                        return null;
                      })}
                    </div>

                    {/* Mobile page indicator - hidden on desktop/tablet */}
                    <span className="sm:hidden text-sm font-medium text-gray-700 px-2">
                      Page {currentPage} of {totalPages}
                    </span>

                    {/* Next Button */}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`px-3 py-2 rounded-lg font-medium transition-colors text-sm ${currentPage === totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-12 text-center">
            <div className="text-gray-400 text-6xl mb-4">🏢</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No companies found</h3>
            <p className="text-gray-500">No companies match your current filters.</p>
          </div>
        )}
      </div>

      {/* Company Detail Modal */}
      {showCompanyModal && selectedCompany && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl max-w-6xl w-full max-h-[90vh] md:max-h-[95vh] overflow-hidden shadow-2xl border border-gray-100 flex flex-col">
            {/* Header with gradient background */}
            <div className="relative bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-6 md:p-8 pr-12 md:pr-16">
              <div className="absolute inset-0 bg-black/10"></div>
              
              {/* Close Button absolute positioned for mobile friendliness */}
              <button
                onClick={() => setShowCompanyModal(false)}
                className="absolute top-4 right-4 z-10 p-2 md:p-3 hover:bg-white/10 rounded-xl transition-colors text-white/80 hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="relative flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
                {/* Company Logo or Initial */}
                <div className="relative flex-shrink-0">
                  {selectedCompany.logoUrl ? (
                    <img
                      src={selectedCompany.logoUrl}
                      alt={selectedCompany.companyName || selectedCompany.name}
                      className="w-20 h-20 rounded-2xl object-cover border-4 border-white/20 shadow-lg"
                    />
                  ) : (
                    <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center text-white font-bold text-3xl border-4 border-white/20 shadow-lg">
                      {(selectedCompany.companyName || selectedCompany.name)?.charAt(0)?.toUpperCase() || 'C'}
                    </div>
                  )}
                  {/* Verification badge */}
                  <div className="absolute -bottom-2 -right-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center border-3 border-white shadow-lg ${selectedCompany.isVerified
                      ? 'bg-green-500'
                      : 'bg-yellow-500'
                      }`}>
                      {selectedCompany.isVerified ? (
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )}
                    </div>
                  </div>
                </div>

                <div className="text-white w-full">
                  <h2 className="text-2xl md:text-3xl font-bold mb-2 break-words pr-4">{selectedCompany.companyName || selectedCompany.name}</h2>
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className="px-3 py-1 md:px-4 md:py-2 bg-white/10 backdrop-blur-sm rounded-full text-xs md:text-sm font-medium border border-white/20">
                      {selectedCompany.industry || 'Technology'}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2 sm:gap-x-6 sm:gap-y-2 text-white/85">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="text-xs md:text-sm">
                        {selectedCompany.city || selectedCompany.state || selectedCompany.country
                          ? `${selectedCompany.city || ''}, ${selectedCompany.state || ''}, ${selectedCompany.country || ''}`.replace(/^,\s*|,\s*$/g, '').replace(/,\s*,/g, ',')
                          : selectedCompany.location || 'Location not specified'
                        }
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <span className="text-xs md:text-sm">{selectedCompany.companySize || 'Size not specified'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                      </svg>
                      <span className="text-xs md:text-sm font-semibold">{selectedCompany.jobCount || 0} Active Jobs</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Content Area */}
            <div className="p-4 md:p-8 bg-gray-50/50 flex-1 overflow-y-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-6">
                  {/* Company Description */}
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">Company Overview</h3>
                    </div>
                    <div className="prose max-w-none text-gray-700 leading-relaxed">
                      {typeof selectedCompany.description === 'string'
                        ? selectedCompany.description.split('\n').map((paragraph, index) => (
                          <p key={index} className="mb-4 text-gray-600">{paragraph}</p>
                        ))
                        : <p className="mb-4 text-gray-600">{selectedCompany.description || 'No description available'}</p>
                      }
                    </div>
                  </div>

                  {/* Benefits & Perks */}
                  {selectedCompany.benefits && selectedCompany.benefits.length > 0 && selectedCompany.benefits.some(b => b.trim()) && (
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                          </svg>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">Benefits & Perks</h3>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {Array.isArray(selectedCompany.benefits)
                          ? selectedCompany.benefits.filter(b => b && b.trim()).map((benefit, index) => (
                            <div key={index} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                              <span className="text-gray-700">{benefit}</span>
                            </div>
                          ))
                          : typeof selectedCompany.benefits === 'string'
                            ? selectedCompany.benefits.split('\n').map((benefit, index) => (
                              <div key={index} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                <span className="text-gray-700">{benefit}</span>
                              </div>
                            ))
                            : <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                              <span className="text-gray-700">{selectedCompany.benefits}</span>
                            </div>
                        }
                      </div>
                    </div>
                  )}

                  {/* Mission, Vision, Culture */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {selectedCompany.mission && selectedCompany.mission.trim() && (
                      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                            <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                          </div>
                          <h4 className="text-lg font-bold text-gray-900">Mission</h4>
                        </div>
                        <p className="text-gray-600 leading-relaxed">{selectedCompany.mission}</p>
                      </div>
                    )}

                    {selectedCompany.vision && selectedCompany.vision.trim() && (
                      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                            <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </div>
                          <h4 className="text-lg font-bold text-gray-900">Vision</h4>
                        </div>
                        <p className="text-gray-600 leading-relaxed">{selectedCompany.vision}</p>
                      </div>
                    )}

                    {selectedCompany.culture && selectedCompany.culture.trim() && (
                      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                            <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                          </div>
                          <h4 className="text-lg font-bold text-gray-900">Culture</h4>
                        </div>
                        <p className="text-gray-600 leading-relaxed">{selectedCompany.culture}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Sidebar - Company Details */}
                <div>
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">Company Details</h3>
                    </div>

                    <div className="space-y-4">
                      {/* Industry */}
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                          </svg>
                          <p className="text-sm font-semibold text-gray-700">Industry</p>
                        </div>
                        <p className="text-gray-900 font-medium">{selectedCompany.industry || 'Not specified'}</p>
                      </div>

                      {/* Location */}
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <p className="text-sm font-semibold text-gray-700">Location</p>
                        </div>
                        <p className="text-gray-900 font-medium">
                          {selectedCompany.city || selectedCompany.state || selectedCompany.country
                            ? `${selectedCompany.city || ''}, ${selectedCompany.state || ''}, ${selectedCompany.country || ''}`.replace(/^,\s*|,\s*$/g, '').replace(/,\s*,/g, ',')
                            : selectedCompany.location || 'Not specified'
                          }
                        </p>
                      </div>

                      {/* Company Size */}
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          <p className="text-sm font-semibold text-gray-700">Company Size</p>
                        </div>
                        <p className="text-gray-900 font-medium">{selectedCompany.companySize || 'Not specified'}</p>
                      </div>

                      {/* Company Type */}
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          <p className="text-sm font-semibold text-gray-700">Company Type</p>
                        </div>
                        <p className="text-gray-900 font-medium">{selectedCompany.companyType || 'Not specified'}</p>
                      </div>

                      {/* Founded Year */}
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V6a2 2 0 012-2h4a2 2 0 012 2v1m-6 0h8m-8 0H6a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V9a2 2 0 00-2-2h-2m-8 0V7" />
                          </svg>
                          <p className="text-sm font-semibold text-gray-700">Founded</p>
                        </div>
                        <p className="text-gray-900 font-medium">{selectedCompany.foundedYear || 'Not specified'}</p>
                      </div>

                      {/* Jobs Posted */}
                      <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                          </svg>
                          <p className="text-sm font-semibold text-blue-700">Active Jobs</p>
                        </div>
                        <p className="text-2xl font-bold text-blue-900">{selectedCompany.jobCount || 0}</p>
                      </div>

                      {/* Profile Completion */}
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-2 mb-3">
                          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                          <p className="text-sm font-semibold text-gray-700">Profile Completion</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex-1 bg-gray-200 rounded-full h-3">
                            <div
                              className="bg-gradient-to-r from-green-500 to-green-600 h-3 rounded-full transition-all duration-500"
                              style={{ width: `${selectedCompany.profileCompletion || 0}%` }}
                            ></div>
                          </div>
                          <span className="text-lg font-bold text-gray-900">{selectedCompany.profileCompletion || 0}%</span>
                        </div>
                      </div>

                      {/* Website */}
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-4 h-4 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9m0 9c-5 0-9-4-9-9s4-9 9-9" />
                          </svg>
                          <p className="text-sm font-semibold text-gray-700">Website</p>
                        </div>
                        {selectedCompany.website && selectedCompany.website.trim() ? (
                          <a
                            href={selectedCompany.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium break-all"
                          >
                            {selectedCompany.website}
                          </a>
                        ) : (
                          <p className="text-gray-600 text-sm">Not provided</p>
                        )}
                      </div>

                      {/* Recruiters */}
                      {selectedCompany.recruiters && selectedCompany.recruiters.length > 0 && (
                        <div className="p-4 bg-gray-50 rounded-xl">
                          <div className="flex items-center gap-2 mb-3">
                            <svg className="w-4 h-4 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            <p className="text-sm font-semibold text-gray-700">Recruiters ({selectedCompany.recruiters.length})</p>
                          </div>
                          <div className="space-y-3">
                            {selectedCompany.recruiters.map((recruiter, index) => (
                              <div key={index} className="bg-white p-3 rounded-lg border border-gray-200">
                                <div className="flex items-center gap-2 mb-1">
                                  <div className="w-6 h-6 bg-pink-100 rounded-full flex items-center justify-center">
                                    <span className="text-xs font-bold text-pink-600">
                                      {recruiter.name?.charAt(0)?.toUpperCase() || 'R'}
                                    </span>
                                  </div>
                                  <p className="font-semibold text-sm text-gray-900">{recruiter.name}</p>
                                </div>
                                <p className="text-xs text-gray-600 mb-1">{recruiter.email}</p>
                                {recruiter.phone && (
                                  <p className="text-xs text-gray-600 mb-1">📞 {recruiter.phone}</p>
                                )}
                                {recruiter.position && (
                                  <p className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded inline-block">
                                    {recruiter.position}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Registration Date */}
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V6a2 2 0 012-2h4a2 2 0 012 2v1m-6 0h8m-8 0H6a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V9a2 2 0 00-2-2h-2m-8 0V7" />
                          </svg>
                          <p className="text-sm font-semibold text-gray-700">Registration Date</p>
                        </div>
                        <p className="text-gray-900 font-medium">{formatDate(selectedCompany.createdAt)}</p>
                      </div>

                      {/* Company Images */}
                      {selectedCompany.companyImages && selectedCompany.companyImages.length > 0 && (
                        <div className="p-4 bg-gray-50 rounded-xl">
                          <div className="flex items-center gap-2 mb-3">
                            <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <p className="text-sm font-semibold text-gray-700">Company Images ({selectedCompany.companyImages.length})</p>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            {selectedCompany.companyImages.slice(0, 4).map((image, index) => (
                              <img
                                key={index}
                                src={image}
                                alt={`Company ${index + 1}`}
                                className="w-full h-20 object-cover rounded-lg border border-gray-200"
                              />
                            ))}
                          </div>
                          {selectedCompany.companyImages.length > 4 && (
                            <p className="text-xs text-gray-500 mt-2">
                              +{selectedCompany.companyImages.length - 4} more images
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminCompanies;
